// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { useLazyQuery, useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import { enqueueSnackbar } from 'notistack';
import * as React from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '../../lib/base';
import type {
  EditRecurringPlantingTraysDataQuery,
  EditRecurringPlantingTraysDataQueryVariables,
  EditRecurringPlantingTraysMutation,
  EditRecurringPlantingTraysMutationVariables,
} from '../../types/__generated__/graphql';
import { createID, validateNumInput } from '../../utils';
import { ExitDialogButton } from '../common/button';
import { ErrorView } from '../common/error';
import { PageLoading } from '../common/loading';

const EDIT_REC_PLANTING_TRAYS_MUT = gql`
  mutation EditRecurringPlantingTrays(
    $recurring_planting_id: Int!
    $new_trays: Int!
  ) {
    editRecurringPlantingTrays(
      input: {
        recurring_planting_id: $recurring_planting_id
        new_trays: $new_trays
      }
    ) {
      updated_recurring_planting {
        id
        recurring_planting_trays
        updated_at
      }
      updated_plantings {
        id
        trays_rounded
        updated_at
      }
    }
  }
`;
const EDIT_REC_PLANTING_TRAYS_DATA = gql`
  query EditRecurringPlantingTraysData($id: ID!) {
    recurring_planting(id: $id) {
      id
      recurring_planting_trays
    }
  }
`;

interface Props {
  readonly recurringPlantingId: number;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSuccess: (shouldRefetchSelf: boolean) => void;
}

export default function EditRecurringPlantingTraysDialog({ recurringPlantingId, isOpen, onClose, onSuccess }: Props) {
  const [editRecurringPlantingTrays, { loading: editSingleOrderLoading }] = useMutation<
    EditRecurringPlantingTraysMutation,
    EditRecurringPlantingTraysMutationVariables
  >(EDIT_REC_PLANTING_TRAYS_MUT);
  const [getRecurringPlantingData, { loading: plantingDataLoading, error: plantingDataError }] = useLazyQuery<
    EditRecurringPlantingTraysDataQuery,
    EditRecurringPlantingTraysDataQueryVariables
  >(EDIT_REC_PLANTING_TRAYS_DATA, {
    defaultOptions: {
      fetchPolicy: 'network-only',
    },
  });

  React.useEffect(() => {
    if (isOpen) {
      void getRecurringPlantingData({
        variables: { id: createID.recurringPlanting(recurringPlantingId) },
        onCompleted: (plantingDataIn) => {
          if (plantingDataIn?.recurring_planting != null) {
            setTrays(plantingDataIn.recurring_planting.recurring_planting_trays.toString());
            setExistingTrays(plantingDataIn.recurring_planting.recurring_planting_trays.toString());
          }
        },
        onError: (errorIn) => {
          enqueueSnackbar(errorIn.message, {
            variant: 'error',
            autoHideDuration: 10000,
          });
        },
      });
    }
  }, [isOpen, recurringPlantingId, getRecurringPlantingData]);

  const disableFields = editSingleOrderLoading || plantingDataLoading;

  const onCloseInternal = () => {
    onClose();
  };

  const onSubmit = (_e?: React.MouseEvent | React.TouchEvent) => {
    void editRecurringPlantingTrays({
      variables: {
        recurring_planting_id: recurringPlantingId,
        new_trays: Number.parseInt(trays),
      },
      onCompleted: (dataIn) => {
        if (dataIn !== undefined && dataIn.editRecurringPlantingTrays != null) {
          enqueueSnackbar('Updated recurring planting trays', { variant: 'success' });
          onSuccess(false);
        } else {
          enqueueSnackbar('Error editing planting trays', { variant: 'error', autoHideDuration: 10000 });
        }
      },
      onError: (errorIn) => {
        enqueueSnackbar(errorIn.message, { variant: 'error', autoHideDuration: 10000 });
      },
    });
  };

  const [trays, setTrays] = React.useState('');
  const [existingTrays, setExistingTrays] = React.useState('');
  const [traysValidation, setTraysValidation] = React.useState('');
  const onChangeTrays = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setTrays(e.target.value);
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => setTraysValidation(msg),
      validateGT: 0,
      isRequired: true,
      validateInt: true,
    });
  };
  const onTraysBlur = React.useCallback((e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => setTraysValidation(msg),
      validateGT: 0,
      isRequired: true,
      validateInt: true,
    });
  }, []);

  const somethingChanged = existingTrays !== trays;
  const isSubmitDisabled: boolean =
    disableFields || !somethingChanged || editSingleOrderLoading || plantingDataLoading || traysValidation !== '';
  const onEnter = () => {
    if (!isSubmitDisabled && !disableFields) {
      onSubmit();
    }
  };

  const dialogContent = plantingDataError ? (
    <ErrorView error={plantingDataError} />
  ) : plantingDataLoading ? (
    <PageLoading />
  ) : (
    <Box display="flex" flexDirection="column" sx={{ m: 1 }} gap={2}>
      <TextField
        aria-describedby="planting-trays"
        id="planting-trays"
        label="New Trays"
        value={trays}
        onChange={onChangeTrays}
        // onEnter={onEnter}
        disabled={disableFields}
        onBlur={onTraysBlur}
        hasSubtext
        subtext={traysValidation}
        error={traysValidation !== ''}
      />
      <Typography>
        Note that changing the trays for a recurring planting will update planned child plantings but not in progress or
        completed child plantings.
      </Typography>
    </Box>
  );

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        onCloseInternal();
      }}
      maxWidth="md"
    >
      <DialogTitle>
        <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Edit Recurring Planting Trays</Typography>
          <ExitDialogButton dontShiftUp onCloseDialog={onCloseInternal} />
        </Box>
      </DialogTitle>
      <DialogContent
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            onEnter();
          }
        }}
        sx={{ padding: 1 }}
      >
        {dialogContent}
      </DialogContent>
      <DialogActions disableSpacing sx={{ flexDirection: { md: 'row', xs: 'column' }, alignItems: 'center' }}>
        <Button
          sx={{ width: { xs: '100%', md: '50%' } }}
          variant="outlined"
          onClick={onCloseInternal}
          disabled={disableFields}
        >
          Cancel
        </Button>
        <Button
          id="submit-planting-button"
          sx={{ width: { xs: '100%', md: '50%' }, ml: { md: 1 }, mt: { xs: 1, md: 0 } }}
          variant="contained"
          onClick={onSubmit}
          type="submit"
          disabled={isSubmitDisabled}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              onEnter();
            }
          }}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
